import React, { Dispatch, SetStateAction, useState } from 'react';
import { ReactComponent as PlusIcon } from '../../images/plus.svg';
import PromptSelectionModal from '../PromptSelectionModal';
import UploadSupportingDocument from '../UploadSupportingDocument';
import Modal from 'react-bootstrap/Modal';
import { Button } from 'react-bootstrap';
import RecordOrUploadModal from '../RecordOrUploadModal';
import { PageBusyLoader } from '../ui/PageBusyLoader';
import axiosInstance from '../../api/api';
import { toast } from 'react-toastify';
import { loadCachedRolePrompts } from '../../utils/rolePromptCache';
import { rankedPromptTexts } from '../../utils/parseRankedAiPromptList';

interface AddNewPromptStepperProps {
  prompts: any;
  setPromptsData: Dispatch<SetStateAction<any>>;
}

const AddNewPromptStepper: React.FC<AddNewPromptStepperProps> = ({ prompts, setPromptsData }) => {
  const [showPicker, setShowPicker] = useState(false);
  const [recordModal, setRecordModal] = useState(false);
  const [showFinishWizard, setShowFinishWizard] = useState(false);
  const [finishStep, setFinishStep] = useState<3 | 4>(3);
  const [promptText, setPromptText] = useState('');
  const [errors, setErrors] = useState({});
  const [videoFiles, setVideoFiles] = useState<{
    [key: number]: File;
  }>({});
  const [documentFiles, setDocumentFiles] = useState<{ [key: number]: File }>({});
  const [currentDescription, setCurrentDescription] = useState('');
  const [loader, setLoader] = useState<boolean>(false);
  const [newPromptKey, setNewPromptKey] = useState<number | null>(null);

  const MAX_PROMPTS = 6;

  const deriveMeta = () => {
    const last = prompts?.[prompts.length - 1];
    return {
      jobTitle: last?.jobTitle ?? last?.job_title ?? '',
      jobIndustry: last?.jobIndustry ?? last?.job_industry ?? '',
      level: last?.level ?? '',
    };
  };

  const [meta, setMeta] = useState(deriveMeta());

  const resetFlow = () => {
    setShowPicker(false);
    setRecordModal(false);
    setShowFinishWizard(false);
    setFinishStep(3);
    setErrors({});
    setVideoFiles({});
    setDocumentFiles({});
    setPromptText('');
    setCurrentDescription('');
    setNewPromptKey(null);
  };

  const hasVideoForPrompt = newPromptKey !== null && Boolean(videoFiles[newPromptKey]);
  const descriptionLength = currentDescription?.length || 0;
  const MAX_DESCRIPTION = 200;

  const handlePromptSelect = (text: string) => {
    const newIndex = prompts?.length || 0;
    setNewPromptKey(newIndex);
    setPromptText(text);
    setMeta(deriveMeta());
    setShowPicker(false);
    setTimeout(() => setRecordModal(true), 100);
  };

  const handleRecordModalClose = () => {
    setRecordModal(false);
    if (newPromptKey !== null && videoFiles[newPromptKey]) {
      setFinishStep(3);
      setShowFinishWizard(true);
      return;
    }
    resetFlow();
  };

  const handlePrevFinishStep = () => {
    if (finishStep === 4) {
      setFinishStep(3);
      return;
    }
    setShowFinishWizard(false);
    setTimeout(() => setRecordModal(true), 100);
  };

  const handleNextFinishStep = () => {
    if (finishStep === 3) {
      setFinishStep(4);
      return;
    }
    void onSubmit();
  };

  const onSubmit = async () => {
    if (newPromptKey === null || !hasVideoForPrompt) {
      toast.error('Add a video response before saving.');
      return;
    }

    setLoader(true);
    try {
      if ((prompts?.length || 0) >= MAX_PROMPTS) {
        toast.error(`You can’t have more than ${MAX_PROMPTS} prompts`);
        return;
      }
      const formData = new FormData();

      const promptData = {
        content: promptText,
        description: currentDescription || '',
        jobTitle: meta.jobTitle,
        jobIndustry: meta.jobIndustry,
        level: meta.level,
        fileId: `file-${newPromptKey}`,
        supportDocumentFileId: documentFiles[newPromptKey] ? `doc-${newPromptKey}` : undefined,
      };

      formData.append('prompts', JSON.stringify([promptData]));
      formData.append(
        `file-${newPromptKey}`,
        videoFiles[newPromptKey],
        videoFiles[newPromptKey].name
      );

      if (documentFiles[newPromptKey]) {
        formData.append(
          `doc-${newPromptKey}`,
          documentFiles[newPromptKey],
          documentFiles[newPromptKey].name
        );
      }

      await axiosInstance.post('prompt', formData);
      toast.success('Prompt added');

      const res = await axiosInstance.get('prompts');
      if (Array.isArray(res.data)) {
        setPromptsData(res.data.slice(0, MAX_PROMPTS));
      }
      window.dispatchEvent(new Event('prompts-updated'));

      resetFlow();
    } catch (err) {
      console.error(err);
      toast.error('Failed to add prompt');
    } finally {
      setLoader(false);
    }
  };

  const isDisabledCreate = (prompts?.length || 0) >= MAX_PROMPTS;

  const handleOpenPromptSelection = () => {
    if (!isDisabledCreate) {
      setCurrentDescription('');
      setMeta(deriveMeta());
      setShowPicker(true);
    }
  };

  const roleSpecificPrompts = rankedPromptTexts(
    loadCachedRolePrompts(meta.jobTitle, meta.jobIndustry, meta.level)
  );

  return (
    <>
      <button
        type="button"
        className={`prompt-stepper-trigger prompt-stepper-trigger--icon-only ${isDisabledCreate ? 'prompt-stepper-trigger--disabled' : ''}`}
        onClick={handleOpenPromptSelection}
        disabled={isDisabledCreate}
        aria-label="Add prompt"
        title={
          isDisabledCreate
            ? `You can’t have more than ${MAX_PROMPTS} prompts`
            : 'Add a new screening prompt'
        }
      >
        <PlusIcon className="prompt-stepper-icon" />
      </button>

      {/* Step 1 — same picker modal as onboarding /create-prompt */}
      <Modal
        show={showPicker}
        onHide={resetFlow}
        centered
        size="lg"
        dialogClassName="prompt-picker-modal"
        scrollable
      >
        <PromptSelectionModal
          jobIndustry={meta.jobIndustry}
          jobTitle={meta.jobTitle}
          level={meta.level}
          roleSpecificPrompts={roleSpecificPrompts}
          onModalClose={() => setShowPicker(false)}
          onPromptSelect={handlePromptSelect}
          editingIndex={newPromptKey}
          setEditingIndex={() => {}}
          promptArray={prompts.map((p: any) => p.content)}
        />
      </Modal>

      {/* Step 2 — record / upload studio (same modal as onboarding) */}
      <Modal
        show={recordModal}
        onHide={handleRecordModalClose}
        centered
        className="recording-studio-modal"
        backdropClassName="recording-studio-modal-backdrop"
      >
        <RecordOrUploadModal
          key={newPromptKey}
          editingIndex={newPromptKey}
          openPromptIndex={newPromptKey}
          recordOrUploadModal={recordModal}
          errors={errors}
          setErrors={setErrors}
          setVideoFiles={setVideoFiles}
          videoFiles={videoFiles}
          documentFiles={documentFiles}
          setDocumentFiles={setDocumentFiles}
          onModalClose={handleRecordModalClose}
          onVideoAttached={() => {
            setRecordModal(false);
            setFinishStep(3);
            setShowFinishWizard(true);
          }}
          hideSubmit
          preferRecordingStudio
          promptText={promptText}
        />
      </Modal>

      {/* Steps 3–4 — optional document + description before save */}
      <Modal
        show={showFinishWizard}
        onHide={resetFlow}
        centered
        size="lg"
        dialogClassName="add-prompt-wizard-modal"
      >
        <Modal.Header closeButton className="add-prompt-wizard-header">
          <div className="add-prompt-wizard-headline">
            <div className="add-prompt-wizard-kicker">Step {finishStep} of 4</div>
            <Modal.Title>
              {finishStep === 3 ? 'Add Supporting Document' : 'Add Prompt Description'}
            </Modal.Title>
            <small>
              {finishStep === 3
                ? 'Attach optional evidence like a portfolio, certification, or case study.'
                : 'Add context that helps recruiters understand your response.'}
            </small>
          </div>
          <div className="add-prompt-wizard-progress" aria-hidden="true">
            {[1, 2, 3, 4].map((step) => (
              <span
                key={step}
                className={`add-prompt-wizard-dot ${finishStep >= step ? 'is-active' : ''}`}
              />
            ))}
          </div>
        </Modal.Header>

        {promptText && <div className="add-prompt-wizard-selected">{promptText}</div>}

        {finishStep === 3 ? (
          <Modal.Body className="add-prompt-wizard-body">
            <div className="add-prompt-wizard-panel">
              <h6 className="mb-2">Strengthen this prompt response</h6>
              <p className="mb-0">
                Optional but recommended: attach one supporting file that validates your experience.
              </p>
            </div>
            <div className="add-prompt-wizard-panel">
              <UploadSupportingDocument
                documentFiles={documentFiles}
                setDocumentFiles={setDocumentFiles}
                openPromptIndex={newPromptKey}
                variant="buttons"
              />
            </div>
          </Modal.Body>
        ) : (
          <Modal.Body className="add-prompt-wizard-body">
            <div className="add-prompt-wizard-panel add-prompt-wizard-panel--description">
              <div className="add-prompt-description-top">
                <label htmlFor="new-prompt-description" className="mb-0">
                  Prompt description (optional)
                </label>
                <small
                  className={descriptionLength > MAX_DESCRIPTION ? 'text-danger' : 'text-muted'}
                >
                  {descriptionLength}/{MAX_DESCRIPTION}
                </small>
              </div>
              <textarea
                id="new-prompt-description"
                className="add-prompt-description-input"
                value={currentDescription}
                onChange={(e) => setCurrentDescription(e.target.value)}
                maxLength={MAX_DESCRIPTION}
                placeholder="Example: In this response, I explain how I reduced CI/CD deployment failures by introducing automated rollout checks and rollback safeguards."
              />
              <p className="add-prompt-description-help">
                Keep it brief and outcome-focused so recruiters can quickly assess your response.
              </p>
            </div>
          </Modal.Body>
        )}

        {loader && (
          <PageBusyLoader pageName="Prompts" overlay className="page-busy-loader--compact" />
        )}

        <Modal.Footer>
          <div className="d-flex justify-content-between w-100 g-10">
            <Button variant="success" onClick={handlePrevFinishStep}>
              Previous
            </Button>
            <Button variant="success" onClick={handleNextFinishStep} disabled={loader}>
              {finishStep === 4 ? 'Save Prompt' : 'Next'}
            </Button>
          </div>
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default AddNewPromptStepper;
